import { useState } from 'react';
import { Filter, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import {
  getDateRangeFromPreset,
  getPresetLabel,
  type DateRangePreset,
} from '@/components/ui/date-range-filter';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { ColumnDateRange } from './utils';

/** The app-standard quick-date set (DateRangeFilter's popover presets). */
const PRESETS: DateRangePreset[] = [
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastFiscalYear',
  'last30',
];

interface DateRangeColumnFilterProps {
  /** Column header label — used in aria-label + the popover heading. */
  columnHeader: string;
  /** Current range. Undefined / both ends empty = no filter. */
  value: ColumnDateRange | undefined;
  /** Fires on every pick / clear so the filter auto-applies. */
  onChange: (next: ColumnDateRange | undefined) => void;
}

/**
 * Popover filter control for `filterVariant: 'dateRange'` columns — the
 * date-column counterpart of ColumnFilter (same trigger affordance and
 * auto-apply behavior), swapping the unique-value checklist for the
 * app-standard quick-date presets (This/Last Month, This Year, Last Fiscal
 * Year, Last 30 Days — `DateRangeFilter`'s popover set) plus inclusive
 * custom From/To pickers. Either end may be left open.
 */
export function DateRangeColumnFilter({
  columnHeader,
  value,
  onChange,
}: DateRangeColumnFilterProps) {
  const { t } = useTranslation('spreadsheet');
  // Preset labels are the shared `dateRange.presets.*` keys in `common`.
  const { t: tCommon } = useTranslation();
  const [open, setOpen] = useState(false);

  const hasActiveFilter = !!(value?.from || value?.to);

  const update = (next: ColumnDateRange) => {
    onChange(next.from || next.to ? next : undefined);
  };

  const handlePresetChange = (preset: string) => {
    const range = getDateRangeFromPreset(preset as DateRangePreset);
    onChange({ from: range.from, to: range.to });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('filter.label') + ': ' + columnHeader}
          title={t('filter.label')}
          className={cn(
            'inline-flex items-center justify-center rounded-sm p-0.5 transition-colors',
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
            hasActiveFilter && 'opacity-100 text-primary',
            !hasActiveFilter && 'text-muted-foreground hover:text-foreground',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="p-3 space-y-3">
          {/* Quick Date Presets — labels come from getPresetLabel (common
              `dateRange.presets.*`), matching every DateRangeFilter surface. */}
          <Select value="" onValueChange={handlePresetChange}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder={t('filter.quickDates')} />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((preset) => (
                <SelectItem key={preset} value={preset}>
                  {getPresetLabel(preset, tCommon)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Custom From/To */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t('filter.from')}</Label>
              <DatePicker
                value={value?.from}
                onChange={(date) => update({ from: date, to: value?.to })}
                className="h-9"
                placeholder={t('filter.from')}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t('filter.to')}</Label>
              <DatePicker
                value={value?.to}
                onChange={(date) => update({ from: value?.from, to: date })}
                className="h-9"
                placeholder={t('filter.to')}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onChange(undefined)}
            disabled={!hasActiveFilter}
          >
            <X className="h-3 w-3 mr-1" />
            {t('filter.clear')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
